import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap';

/**
 * 
 * @param hasUnsavedChanges boolean to trigger the modal 
 * @returns a modal to confirm leaving with unsaved changes
 * @info listens to browser changes like reload, back button, or tab closing
 */
const UnsavedChanges = ({ hasUnsavedChanges }) => {
  const history = useHistory();
  const [showModal, setShowModal] = useState(false);
  const [pendingLocation, setPendingLocation] = useState(null);
  const [unblock, setUnblock] = useState(() => () => {});

  useEffect(() => {
    // handle navigation within the app
    const unblockNavigation = history.block((nextLocation) => {
      if (hasUnsavedChanges) {
        setShowModal(true);
        setPendingLocation(nextLocation);
        return false; // block the navigation
      }
      return true; // allow the navigation
    });

    setUnblock(() => unblockNavigation);

    return () => {
      unblockNavigation(); // clean up on unmount
    };
  }, [hasUnsavedChanges, history]);

  useEffect(() => {
    // handle browser reload or tab close
    const handleBeforeUnload = (event) => {
      if (hasUnsavedChanges) {
        event.preventDefault();
        event.returnValue = ''; // required for Chrome to display the warning
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  const handleConfirmLeave = () => {
    setShowModal(false);
    const targetLocation = pendingLocation;
    setPendingLocation(null);
    unblock();
    history.push(`${targetLocation.pathname}${targetLocation.hash}`);
  };

  const handleCancelLeave = () => {
    setShowModal(false);
    setPendingLocation(null);
  };

  return (
    <Modal isOpen={showModal} centered backdrop>
      <ModalHeader>Unsaved Changes</ModalHeader>
      <ModalBody>
        Warning: Your changes have not been saved. Please save your changes before proceeding or your selections will be lost.
      </ModalBody>
      <ModalFooter>
        <Button onClick={handleConfirmLeave}>
          Continue
        </Button>
        <Button className="promote" onClick={handleCancelLeave}>
          Return To Editing
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default UnsavedChanges;

/**
 * @param showModal show/hide boolean
 * @param confirmLeave confirm function
 * @param cancelLeave cancel function
 * @returns the same modal but manually triggered
 */
export const UnsavedChangesManual = (props) => {
  return (
    <Modal isOpen={props.showModal} centered backdrop>
      <ModalHeader>Unsaved Changes</ModalHeader>
      <ModalBody>
        Warning: Your changes have not been saved. Please save your changes before proceeding or your selections will be lost.
      </ModalBody>
      <ModalFooter>
        <Button onClick={props.confirmLeave}>
          Continue
        </Button>
        <Button className="promote" onClick={props.cancelLeave}>
          Return To Editing
        </Button>
      </ModalFooter>
    </Modal>
  );
}